//replaces single-metric.html
//sw-single-metric.js

import type { FC } from "react";
import { useEffect, useState } from "react";

import classNames from "classnames";

import { ChangeCellTemplateV2 } from "app/components/single-metric/cell-templates/ChangeCellTemplate-v2";
import { ChangeInvertedCellTemplateV2 } from "app/components/single-metric/cell-templates/ChangeInvertedCellTemplate-v2";
import { DefaultHeaderTemplateV2 } from "app/components/single-metric/cell-templates/DefaultHeaderTemplate-v2";
import { LargeNumberCellTemplateV2 } from "app/components/single-metric/cell-templates/LargeNumberCellTemplate-v2";
import { LargeSingleNumberCellTemplateV2 } from "app/components/single-metric/cell-templates/LargeSingleNumberCellTemplate-v2";
import { NumberWithChangeCellTemplate } from "app/components/single-metric/cell-templates/NumberWithChangeCellTemplate";
import { SimpleValueCellTemplate } from "app/components/single-metric/cell-templates/SimpleValueCellTemplate";
import { TrendLineCellTemplateV2 } from "app/components/single-metric/cell-templates/TrendLineCellTemplate-v2";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import { HighChartsConfig } from "app/services/HighChartsConfig";

interface ISingleMetricTemplateV2 extends IDashboardWidgetInterface {
    rows: any;
    options: any;
}

export const SingleMetricTemplateV2: FC<ISingleMetricTemplateV2> = (props) => {
    const { rows, widget, options, data } = props;

    const [chartConfig, setChartConfig] = useState(null);
    const [hasTrendLine, setHasTrendLine] = useState(false);

    useEffect(() => {
        if (!options.template) {
            rows.forEach(function (row) {
                if (row.cellTemplate.indexOf("trend-line") > 0) {
                    const fieldData = data[row.field];
                    const isWindow = widget.apiParams.isWindow;
                    if (fieldData && fieldData.length > 1 && !isWindow) {
                        setChartConfig(HighChartsConfig.getInstance().trendLineWidget(fieldData, rows[0].format));
                        setHasTrendLine(true);
                    } else {
                        setChartConfig(HighChartsConfig.getInstance().trendLineWidget([], null));
                        setHasTrendLine(false);
                    }
                }
            });
        }
    }, []);

    const getHeader = (row) => {
        if (!row.headerTemplate) {
            return null;
        }
        const name = row.headerTemplate;
        const fileNameStartPosition = name.lastIndexOf("/");
        if (fileNameStartPosition < 0) {
            return row.headerTemplate;
        }
        const templateName = name.substring(fileNameStartPosition + 1, name.length - 5);
        switch (templateName) {
            case "default-header":
                return <DefaultHeaderTemplateV2 row={row} />;
            default:
                return <p>{row.headerTemplate}</p>;
        }
    };

    const getCell = (row) => {
        const name = row.cellTemplate;
        const fileNameStartPosition = name.lastIndexOf("/");
        const templateName = name.substring(fileNameStartPosition + 1, name.length - 5);
        switch (templateName) {
            case "trend-line":
                return (
                    <TrendLineCellTemplateV2
                        hasTrendLine={hasTrendLine}
                        chatConfig={chartConfig}
                    />
                );
            case "change-inverted":
                return (
                    <ChangeInvertedCellTemplateV2
                        data={data}
                        row={row}
                    />
                );
            case "change":
                return (
                    <ChangeCellTemplateV2
                        data={data}
                        row={row}
                    />
                );
            case "large-number":
                return (
                    <LargeNumberCellTemplateV2
                        data={data}
                        row={row}
                    />
                );
            case "large-single-number":
                return (
                    <LargeSingleNumberCellTemplateV2
                        data={data}
                        row={row}
                    />
                );
            case "number-with-change":
                return (
                    <NumberWithChangeCellTemplate
                        data={data}
                        row={row}
                    />
                );
            case "simple-value":
                return (
                    <SimpleValueCellTemplate
                        data={data}
                        row={row}
                    />
                );
            default:
                return <p>{row.cellTemplate}</p>;
        }
    };

    return (
        <div className="swTable swTable--singleMetric">
            {rows?.map((row, index) => {
                return (
                    <div
                        key={index}
                        className={classNames("swTable-row", row.rowClass)}
                    >
                        {row.headerTemplate ? <div className="swTable-cell">{getHeader(row)}</div> : null}
                        <div className={"swTable-cell singleMetric-cell--value"}>{getCell(row)}</div>
                    </div>
                );
            })}
        </div>
    );
};
